import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Button from '@amzn/meridian/button';
import DataTable, { DataTableHead, DataTableBody, DataTableRow, DataTableCell, DataTableHeader } from '@amzn/meridian/data-table';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import Box from '@amzn/meridian/box';
import DatePicker from '@amzn/meridian/date-picker';
import Loader from '@amzn/meridian/loader';
import Menu, { MenuItem } from '@amzn/meridian/menu';
import Toggle from '@amzn/meridian/toggle';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Tooltip from '@amzn/meridian/tooltip';
import Icon from '@amzn/meridian/icon';
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import { DEFAULT_SHIFT_OPTIONS } from '../../../../utils/constants';
import { getClockWindowNameBasedOnSelectedOption } from '../../../../services/shiftPlanService';
import { getFlowMonitoringData, compareFlowMonitoringData } from '../api';
import { GetFlowMonitoringDataOutput, CompareFlowMonitoringDataOutput, MonitoringEntityIdentityMetricsData, MonitoringEntityIdentityComparisonData } from '../model';
import { MonitoringEntityType, MetricName } from '../constants';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { selectStationCode, setStationCode } from '../../../../store/slices/shiftPlanSlice';
import { showError } from '../../../../store/slices/errorSlice';
import { formatMetricValue, getStatusColor } from '../utils';
import VolumeTargetCardsRow from './VolumeTargetCardsRow';

interface FclmTableRow {
    processPath: string;
    headcount: number;
    actualUph: number;
    actualVolume: number;
    hours: number;
}

const transformShiftTotalsToRows = (data: MonitoringEntityIdentityMetricsData[]): FclmTableRow[] => {
    return data.map((entity) => {
        const getMetric = (name: MetricName) =>
            Math.round((entity.metricDataList.find((m) => m.metricName === name)?.metricValue ?? 0) * 100) / 100;
        return {
            processPath: entity.monitoringEntityIdentity.processPath?.displayName || entity.monitoringEntityIdentity.processPath?.functionName || '',
            headcount: getMetric(MetricName.HEADCOUNT),
            actualUph: getMetric(MetricName.UPH),
            actualVolume: getMetric(MetricName.VOLUME_PROCESSED),
            hours: getMetric(MetricName.HOURS),
        };
    });
};

const ACTION_OPTIONS = [
    { value: 'fclmTarget', label: 'FCLM Actuals' },
    { value: 'progressReport', label: 'Shift Progress Report' },
];

interface ColumnDef {
    key: string;
    label: string;
}

const ALL_COLUMNS: Record<string, ColumnDef> = {
    processPath: { key: 'processPath', label: 'Process Path' },
    headcount: { key: 'headcount', label: 'Headcount' },
    actualUph: { key: 'actualUph', label: 'Actual UPH' },
    expectedUph: { key: 'expectedUph', label: 'Expected UPH' },
    uphDelta: { key: 'uphDelta', label: 'UPH Delta' },
    uphVariance: { key: 'uphVariance', label: 'UPH Variance' },
    actualVolume: { key: 'actualVolume', label: 'Actual Volume' },
    expectedVolume: { key: 'expectedVolume', label: 'Expected Volume' },
    volumeDelta: { key: 'volumeDelta', label: 'Volume Delta' },
    hours: { key: 'hours', label: 'Hours' },
    status: { key: 'status', label: 'Status' },
};

const FCLM_COLUMN_KEYS = ['processPath', 'headcount', 'actualUph', 'actualVolume', 'hours'] as const;
const FCLM_COLUMNS = FCLM_COLUMN_KEYS.map((k) => ALL_COLUMNS[k]);

const PROGRESS_COLUMN_KEYS = [
    'processPath', 'headcount',
    'actualUph', 'expectedUph', 'uphDelta', 'uphVariance',
    'actualVolume', 'expectedVolume', 'volumeDelta', 'status',
] as const;
const PROGRESS_COLUMNS = PROGRESS_COLUMN_KEYS.map((k) => ALL_COLUMNS[k]);

const Dashboard: React.FC = () => {
    const dispatch = useAppDispatch();
    const stationCode = useAppSelector(selectStationCode);

    // Fallback: read stationCode from URL query params if Redux is empty
    useEffect(() => {
        if (!stationCode) {
            const searchParams = new URLSearchParams(window.location.search);
            const stationCodeParam = searchParams.get('stationCode');
            if (stationCodeParam) {
                dispatch(setStationCode({ stationCode: stationCodeParam }));
            }
        }
    }, [dispatch, stationCode]);

    const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
    const [shiftType, setShiftType] = useState<string>(DEFAULT_SHIFT_OPTIONS[0].value);
    const [selectedAction, setSelectedAction] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
    const [sortDirection, setSortDirection] = useState<'ascending' | 'descending' | undefined>(undefined);
    const [fclmData, setFclmData] = useState<FclmTableRow[]>([]);
    const [apiResponse, setApiResponse] = useState<GetFlowMonitoringDataOutput | null>(null);
    const [compareResponse, setCompareResponse] = useState<CompareFlowMonitoringDataOutput | null>(null);
    const [hourlyView, setHourlyView] = useState(false);
    const [selectedMetric, setSelectedMetric] = useState<string>(MetricName.UPH);

    const isProgressReport = selectedAction === 'progressReport';

    const sortedData = useMemo(() => {
        if (!sortColumn) return fclmData;
        return [...fclmData].sort((a, b) => {
            const aVal = a[sortColumn as keyof FclmTableRow];
            const bVal = b[sortColumn as keyof FclmTableRow];
            const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            return sortDirection === 'ascending' ? cmp : -cmp;
        });
    }, [sortColumn, sortDirection, fclmData]);

    const handleSort = ({ sortColumn: col, sortDirection: dir }: { sortColumn: string; sortDirection: 'ascending' | 'descending' | 'none' }) => {
        setSortColumn(dir === 'none' ? undefined : col);
        setSortDirection(dir === 'none' ? undefined : dir);
    };

    const handleActionChange = useCallback(async (action: string) => {
        setSelectedAction(action);
        setLoading(true);
        setDataLoaded(false);
        setSortColumn(undefined);
        setSortDirection(undefined);

        if (!stationCode) {
            dispatch(showError({
                title: "Warehouse ID Required",
                message: "Please select a valid warehouse ID from the dropdown"
            }));
            setLoading(false);
            return;
        }

        if (action === 'fclmTarget') {
            try {
                const response = await getFlowMonitoringData({
                    warehouseId: stationCode,
                    ofdDate: selectedDate,
                    shiftType: getClockWindowNameBasedOnSelectedOption(shiftType),
                    monitoringEntityType: MonitoringEntityType.PROCESS_PATH,
                    metricsNameList: [MetricName.UPH, MetricName.VOLUME_PROCESSED, MetricName.HEADCOUNT, MetricName.HOURS],
                });
                setApiResponse(response);
                setFclmData(transformShiftTotalsToRows(response.shiftTotals));
                setDataLoaded(true);
            } catch (err: any) {
                dispatch(showError({ message: err.message || 'Failed to fetch FCLM data', title: 'API Error' }));
            } finally {
                setLoading(false);
            }
        } else {
            try {
                const response = await compareFlowMonitoringData({
                    warehouseId: stationCode,
                    ofdDate: selectedDate,
                    shiftType: getClockWindowNameBasedOnSelectedOption(shiftType),
                    monitoringEntityType: MonitoringEntityType.PROCESS_PATH,
                });
                setCompareResponse(response);
                setDataLoaded(true);
            } catch (err: any) {
                dispatch(showError({ message: err.message || 'Failed to fetch comparison data', title: 'API Error' }));
            } finally {
                setLoading(false);
            }
        }
    }, [selectedDate, shiftType, stationCode, dispatch]);

    return (
        <Column spacing="500" spacingInset="500">
            <Row spacing="500" alignmentVertical="bottom" widths="fit">
                <Column spacing="300">
                    <Text tag="label" type="b400">Date</Text>
                    <DatePicker value={selectedDate} onChange={setSelectedDate} width={220} size="large" />
                </Column>
                <Column spacing="300">
                    <Text tag="label" type="b400">Shift</Text>
                    <Select value={shiftType} onChange={setShiftType} width={220} size="large">
                        {DEFAULT_SHIFT_OPTIONS.map((opt) => (
                            <SelectOption key={opt.value} value={opt.value} label={opt.label} />
                        ))}
                    </Select>
                </Column>
                <Column spacing="300">
                    <Button ref={buttonRef} onClick={() => setMenuOpen(!menuOpen)}>
                        Select Action
                    </Button>
                    <Menu
                        anchorNode={buttonRef.current ?? undefined}
                        open={menuOpen}
                        position="bottom"
                        onClose={() => setMenuOpen(false)}
                    >
                        {ACTION_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} onClick={() => { setMenuOpen(false); handleActionChange(opt.value); }}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </Column>
            </Row>

            {loading && (
                <Column alignmentHorizontal="center" spacing="300" spacingInset="500">
                    <Loader size="large" />
                    <Text type="b300" color="secondary">Loading data...</Text>
                </Column>
            )}

            {dataLoaded && !loading && (
                <>
                    <Row alignmentHorizontal="justify" alignmentVertical="center">
                        <Heading level={2} type="h400">
                            {isProgressReport ? 'Progress Report' : 'FCLM Actuals Report'}
                        </Heading>
                        <Toggle checked={hourlyView} onChange={setHourlyView}>Hourly View</Toggle>
                    </Row>

                    {isProgressReport && (
                        <VolumeTargetCardsRow volumeTargetComparisons={compareResponse?.volumeTargetComparisons} />
                    )}

                    <Box overflowX="auto" overflowY="auto" maxHeight="600px">
                        {isProgressReport && !hourlyView ? (
                            <DataTable
                                showRowDividers
                                showColumnDividers
                                width="100%"
                            >
                                <DataTableHead backgroundColor="secondary">
                                    <DataTableRow>
                                        <DataTableHeader>Process Path</DataTableHeader>
                                        <DataTableHeader>
                                            <Row spacing="200" alignmentVertical="center" widths="fit">
                                                <Text type="b300">Metrics</Text>
                                            </Row>
                                        </DataTableHeader>
                                        <DataTableHeader>Actual</DataTableHeader>
                                        <DataTableHeader>Expected</DataTableHeader>
                                        <DataTableHeader>Delta</DataTableHeader>
                                        <DataTableHeader>Variance</DataTableHeader>
                                        <DataTableHeader>Status</DataTableHeader>
                                    </DataTableRow>
                                </DataTableHead>
                                <DataTableBody>
                                    {compareResponse?.shiftTotalsComparison.map((entity) => {
                                        const displayName = entity.monitoringEntityIdentity.processPath?.displayName || entity.monitoringEntityIdentity.processPath?.functionName || '';
                                        const uph = entity.metricsComparisonList.find((m) => m.metricName === MetricName.UPH);
                                        const vol = entity.metricsComparisonList.find((m) => m.metricName === MetricName.VOLUME_PROCESSED);
                                        const hc = entity.metricsComparisonList.find((m) => m.metricName === MetricName.HEADCOUNT);
                                        const hours = entity.metricsComparisonList.find((m) => m.metricName === MetricName.HOURS);
                                        return (
                                            <React.Fragment key={displayName}>
                                                <DataTableRow>
                                                    <DataTableCell rowSpan={4}>{displayName}</DataTableCell>
                                                    <DataTableCell>UPH</DataTableCell>
                                                    <DataTableCell><Text type="b300" color={uph?.comparisonStatus === 'AHEAD' ? 'success' : uph?.comparisonStatus === 'CRITICAL' ? 'error' : undefined}>{formatMetricValue(uph?.actualValue ?? 0)}</Text></DataTableCell>
                                                    <DataTableCell>{formatMetricValue(uph?.expectedValue ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(uph?.delta ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(uph?.variancePercentage ?? 0)}%</DataTableCell>
                                                    <DataTableCell><span style={{ color: getStatusColor(uph?.comparisonStatus), fontWeight: 'bold' }}>{uph?.comparisonStatus ?? ''}</span></DataTableCell>
                                                </DataTableRow>
                                                <DataTableRow>
                                                    <DataTableCell>Volume</DataTableCell>
                                                    <DataTableCell><Text type="b300" color={vol?.comparisonStatus === 'AHEAD' ? 'success' : vol?.comparisonStatus === 'CRITICAL' ? 'error' : undefined}>{formatMetricValue(vol?.actualValue ?? 0)}</Text></DataTableCell>
                                                    <DataTableCell>{formatMetricValue(vol?.expectedValue ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(vol?.delta ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(vol?.variancePercentage ?? 0)}%</DataTableCell>
                                                    <DataTableCell><span style={{ color: getStatusColor(vol?.comparisonStatus), fontWeight: 'bold' }}>{vol?.comparisonStatus ?? ''}</span></DataTableCell>
                                                </DataTableRow>
                                                <DataTableRow>
                                                    <DataTableCell>HC</DataTableCell>
                                                    <DataTableCell><Text type="b300" color={hc?.comparisonStatus === 'AHEAD' ? 'success' : hc?.comparisonStatus === 'CRITICAL' ? 'error' : undefined}>{formatMetricValue(hc?.actualValue ?? 0)}</Text></DataTableCell>
                                                    <DataTableCell>{formatMetricValue(hc?.expectedValue ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(hc?.delta ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(hc?.variancePercentage ?? 0)}%</DataTableCell>
                                                    <DataTableCell><span style={{ color: getStatusColor(hc?.comparisonStatus), fontWeight: 'bold' }}>{hc?.comparisonStatus ?? ''}</span></DataTableCell>
                                                </DataTableRow>
                                                <DataTableRow>
                                                    <DataTableCell>Hours</DataTableCell>
                                                    <DataTableCell><Text type="b300" color={hours?.comparisonStatus === 'AHEAD' ? 'success' : hours?.comparisonStatus === 'CRITICAL' ? 'error' : undefined}>{formatMetricValue(hours?.actualValue ?? 0)}</Text></DataTableCell>
                                                    <DataTableCell>{formatMetricValue(hours?.expectedValue ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(hours?.delta ?? 0)}</DataTableCell>
                                                    <DataTableCell>{formatMetricValue(hours?.variancePercentage ?? 0)}%</DataTableCell>
                                                    <DataTableCell><span style={{ color: getStatusColor(hours?.comparisonStatus), fontWeight: 'bold' }}>{hours?.comparisonStatus ?? ''}</span></DataTableCell>
                                                </DataTableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </DataTableBody>
                            </DataTable>
                        ) : isProgressReport && hourlyView && compareResponse ? (
                            <DataTable
                                showRowDividers
                                showColumnDividers
                                width="100%"
                            >
                                <DataTableHead backgroundColor="secondary">
                                    <DataTableRow>
                                        <DataTableHeader stickyColumn width={150}>Process Path</DataTableHeader>
                                        <DataTableHeader width={100}>Metrics</DataTableHeader>
                                        {compareResponse.hourlyShiftComparisonDataList.map((window) => (
                                            <DataTableHeader key={window.startTime}>
                                                {window.startTime} - {window.endTime}
                                            </DataTableHeader>
                                        ))}
                                    </DataTableRow>
                                </DataTableHead>
                                <DataTableBody>
                                    {compareResponse.shiftTotalsComparison.map((entity) => {
                                        const displayName = entity.monitoringEntityIdentity.processPath?.displayName || entity.monitoringEntityIdentity.processPath?.functionName || '';
                                        return (
                                            <React.Fragment key={displayName}>
                                                <DataTableRow>
                                                    <DataTableCell stickyColumn rowSpan={4}>{displayName}</DataTableCell>
                                                    <DataTableCell>
                                                        <Row spacing="200" alignmentVertical="center" widths="fit">
                                                            <Text type="b300">UPH</Text>
                                                            <Tooltip title="Actual | Expected" position="end">
                                                                <Icon tokens={infoKnockoutTokens}>Info</Icon>
                                                            </Tooltip>
                                                        </Row>
                                                    </DataTableCell>
                                                    {compareResponse.hourlyShiftComparisonDataList.map((window) => {
                                                        const windowEntity = window.monitoringEntityIdentitiesComparisonDataList.find(
                                                            (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === displayName
                                                        );
                                                        const metric = windowEntity?.metricsComparisonList.find((m) => m.metricName === MetricName.UPH);
                                                        return <DataTableCell key={window.startTime}><Text type="b300" fontFamily="amazonEmberMonospace"><span style={{ color: getStatusColor(metric?.comparisonStatus) }}>{formatMetricValue(metric?.actualValue ?? 0)}</span>{' | '}{formatMetricValue(metric?.expectedValue ?? 0)}</Text></DataTableCell>;
                                                    })}
                                                </DataTableRow>
                                                <DataTableRow>
                                                    <DataTableCell>
                                                        <Row spacing="200" alignmentVertical="center" widths="fit">
                                                            <Text type="b300">Volume</Text>
                                                            <Tooltip title="Actual | Expected" position="end">
                                                                <Icon tokens={infoKnockoutTokens} >Info</Icon>
                                                            </Tooltip>
                                                        </Row>
                                                    </DataTableCell>
                                                    {compareResponse.hourlyShiftComparisonDataList.map((window) => {
                                                        const windowEntity = window.monitoringEntityIdentitiesComparisonDataList.find(
                                                            (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === displayName
                                                        );
                                                        const metric = windowEntity?.metricsComparisonList.find((m) => m.metricName === MetricName.VOLUME_PROCESSED);
                                                        return <DataTableCell key={window.startTime}><Text type="b300" fontFamily="amazonEmberMonospace"><span style={{ color: getStatusColor(metric?.comparisonStatus) }}>{formatMetricValue(metric?.actualValue ?? 0)}</span>{' | '}{formatMetricValue(metric?.expectedValue ?? 0)}</Text></DataTableCell>;
                                                    })}
                                                </DataTableRow>
                                                <DataTableRow>
                                                    <DataTableCell>
                                                        <Row spacing="200" alignmentVertical="center" widths="fit">
                                                            <Text type="b300">HC</Text>
                                                            <Tooltip title="Actual | Expected" position="end">
                                                                <Icon tokens={infoKnockoutTokens}>Info</Icon>
                                                            </Tooltip>
                                                        </Row>
                                                    </DataTableCell>
                                                    {compareResponse.hourlyShiftComparisonDataList.map((window) => {
                                                        const windowEntity = window.monitoringEntityIdentitiesComparisonDataList.find(
                                                            (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === displayName
                                                        );
                                                        const metric = windowEntity?.metricsComparisonList.find((m) => m.metricName === MetricName.HEADCOUNT);
                                                        return <DataTableCell key={window.startTime}><Text type="b300" fontFamily="amazonEmberMonospace"><span style={{ color: getStatusColor(metric?.comparisonStatus) }}>{formatMetricValue(metric?.actualValue ?? 0)}</span>{' | '}{formatMetricValue(metric?.expectedValue ?? 0)}</Text></DataTableCell>;
                                                    })}
                                                </DataTableRow>
                                                <DataTableRow>
                                                    <DataTableCell>
                                                        <Row spacing="200" alignmentVertical="center" widths="fit">
                                                            <Text type="b300">Hours</Text>
                                                            <Tooltip title="Actual | Expected" position="end">
                                                                <Icon tokens={infoKnockoutTokens}>Info</Icon>
                                                            </Tooltip>
                                                        </Row>
                                                    </DataTableCell>
                                                    {compareResponse.hourlyShiftComparisonDataList.map((window) => {
                                                        const windowEntity = window.monitoringEntityIdentitiesComparisonDataList.find(
                                                            (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === displayName
                                                        );
                                                        const metric = windowEntity?.metricsComparisonList.find((m) => m.metricName === MetricName.HOURS);
                                                        return <DataTableCell key={window.startTime}><Text type="b300" fontFamily="amazonEmberMonospace"><span style={{ color: getStatusColor(metric?.comparisonStatus) }}>{formatMetricValue(metric?.actualValue ?? 0)}</span>{' | '}{formatMetricValue(metric?.expectedValue ?? 0)}</Text></DataTableCell>;
                                                    })}
                                                </DataTableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                </DataTableBody>
                            </DataTable>
                        ) : hourlyView && apiResponse ? (
                            <DataTable
                                showRowDividers
                                showColumnDividers
                                width="100%"
                            >
                                <DataTableHead backgroundColor="secondary">
                                    <DataTableRow>
                                        <DataTableHeader stickyColumn width={150}>Process Path</DataTableHeader>
                                        <DataTableHeader width={90}>Metrics</DataTableHeader>
                                        {apiResponse.hourlyShiftMonitoringDataList.map((window) => (
                                            <DataTableHeader key={window.startTime}>
                                                {window.startTime} - {window.endTime}
                                            </DataTableHeader>
                                        ))}
                                    </DataTableRow>
                                </DataTableHead>
                                <DataTableBody>
                                    {fclmData.map((row) => (
                                        <React.Fragment key={row.processPath}>
                                            <DataTableRow>
                                                <DataTableCell stickyColumn rowSpan={4}>{row.processPath}</DataTableCell>
                                                <DataTableCell>UPH</DataTableCell>
                                                {apiResponse.hourlyShiftMonitoringDataList.map((window) => {
                                                    const entity = window.monitoringEntityIdentityMetricsDataList.find(
                                                        (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === row.processPath
                                                    );
                                                    const val = Math.round((entity?.metricDataList.find((m) => m.metricName === MetricName.UPH)?.metricValue ?? 0) * 100) / 100;
                                                    return <DataTableCell key={window.startTime}>{val}</DataTableCell>;
                                                })}
                                            </DataTableRow>
                                            <DataTableRow>
                                                <DataTableCell>Volume</DataTableCell>
                                                {apiResponse.hourlyShiftMonitoringDataList.map((window) => {
                                                    const entity = window.monitoringEntityIdentityMetricsDataList.find(
                                                        (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === row.processPath
                                                    );
                                                    const val = Math.round((entity?.metricDataList.find((m) => m.metricName === MetricName.VOLUME_PROCESSED)?.metricValue ?? 0) * 100) / 100;
                                                    return <DataTableCell key={window.startTime}>{val}</DataTableCell>;
                                                })}
                                            </DataTableRow>
                                            <DataTableRow>
                                                <DataTableCell>Headcount</DataTableCell>
                                                {apiResponse.hourlyShiftMonitoringDataList.map((window) => {
                                                    const entity = window.monitoringEntityIdentityMetricsDataList.find(
                                                        (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === row.processPath
                                                    );
                                                    const val = Math.round((entity?.metricDataList.find((m) => m.metricName === MetricName.HEADCOUNT)?.metricValue ?? 0) * 100) / 100;
                                                    return <DataTableCell key={window.startTime}>{val}</DataTableCell>;
                                                })}
                                            </DataTableRow>
                                            <DataTableRow>
                                                <DataTableCell>Hours</DataTableCell>
                                                {apiResponse.hourlyShiftMonitoringDataList.map((window) => {
                                                    const entity = window.monitoringEntityIdentityMetricsDataList.find(
                                                        (e) => (e.monitoringEntityIdentity.processPath?.displayName || e.monitoringEntityIdentity.processPath?.functionName) === row.processPath
                                                    );
                                                    const val = Math.round((entity?.metricDataList.find((m) => m.metricName === MetricName.HOURS)?.metricValue ?? 0) * 100) / 100;
                                                    return <DataTableCell key={window.startTime}>{val}</DataTableCell>;
                                                })}
                                            </DataTableRow>
                                        </React.Fragment>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        ) : (
                            <DataTable
                                showRowDividers
                                showStripes
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                width="100%"
                            >
                                <DataTableHead backgroundColor="secondary">
                                    <DataTableRow>
                                        {FCLM_COLUMNS.map((col) => (
                                            <DataTableHeader key={col.key} sortColumn={col.key}>{col.label}</DataTableHeader>
                                        ))}
                                    </DataTableRow>
                                </DataTableHead>
                                <DataTableBody>
                                    {sortedData.map((row) => (
                                        <DataTableRow key={row.processPath} highlightOnHover>
                                            {FCLM_COLUMNS.map((col) => (
                                                <DataTableCell key={col.key}>{row[col.key as keyof FclmTableRow]}</DataTableCell>
                                            ))}
                                        </DataTableRow>
                                    ))}
                                </DataTableBody>
                            </DataTable>
                        )}
                    </Box>

                    <Row spacing="400" alignmentVertical="center" alignmentHorizontal="justify">
                        <Text type="b300">
                            Showing {sortedData.length} out of {fclmData.length} Process Paths
                        </Text>
                        <Row spacing="400" alignmentVertical="center" widths="fit">
                            <Box type="outline" spacingInset="300">
                                <Row spacing="300" alignmentVertical="center">
                                    <Text type="b300" color="secondary">Window</Text>
                                    <Text type="b300">13:00 - 14:00 IST</Text>
                                </Row>
                            </Box>
                            <Button type="secondary" onClick={() => handleActionChange(selectedAction)}>Refresh Now</Button>
                        </Row>
                    </Row>
                </>
            )}
        </Column>
    );
};

export default Dashboard;
